import { captureException } from "./init";

// Email validation: 3-128 chars, basic format: localpart@domain.tld
const emailRegex = /^(?=\S{3,128}$)[^\s\\;,:@]+@[^\s\\;,:\.@]{1}[^\s\\;,:@]*\.[^\s\\;,:0-9@]+$/;

function sanitizeEmailPathPart(part: string): string | undefined {
  const suffixIndex = part.search(/[?#]/);
  const pathPart = suffixIndex < 0 ? part : part.slice(0, suffixIndex);
  let isEmail: boolean;
  try {
    isEmail = emailRegex.test(decodeURIComponent(pathPart));
    // eslint-disable-next-line no-catch-all/no-catch-all -- malformed percent-encoding should fall back to testing the raw path
  } catch {
    isEmail = emailRegex.test(pathPart);
  }
  if (!isEmail) {
    return;
  }
  return suffixIndex < 0 ? "{email}" : `{email}${part.slice(suffixIndex)}`;
}

// Vite/rollup name bundled assets `[name]-[hash].(js|css)`, with an 8-character hash from rollup's
// base64-ish alphabet (`0-9a-zA-Z_$`) — NOT hex. The hash lives inside the filename rather than in
// its own path segment, so the id-segment rules below never see it, and every deploy that rebuilds a
// chunk mints a brand new fingerprint for the same logical asset.
const ASSET_HASH_SUFFIX = /-([0-9a-zA-Z_$]{8,})\.(js|mjs|cjs|css)$/;

// Requiring one character outside [a-z] keeps a real word (`index-optional.css`) from being read as
// a hash. A generated hash that happens to be all lowercase letters is ~0.03% of cases.
const LOOKS_GENERATED = /[0-9A-Z_$]/;

/** Replace a bundler content hash in an asset filename with a placeholder, so the same logical
 * asset keeps one identity across deploys. Returns the input unchanged when it is not a hashed
 * asset filename. */
export function stripAssetHash(part: string): string {
  return part.replace(ASSET_HASH_SUFFIX, (match, hash: string, extension: string) =>
    LOOKS_GENERATED.test(hash) ? `-{hash}.${extension}` : match,
  );
}

export function sanitizeIds(url: string) {
  try {
    const pathParts = url.split("/");
    const sanitizedParts = pathParts.map((part, index, array) => {
      const sanitizedEmailPathPart = sanitizeEmailPathPart(part);
      if (sanitizedEmailPathPart) {
        return sanitizedEmailPathPart;
      }
      const withoutAssetHash = stripAssetHash(part);
      if (withoutAssetHash !== part) {
        return withoutAssetHash;
      }
      if (!/^[0-9a-f]{24}$/.test(part) && !/^[0-9]+$/.test(part)) {
        return part;
      }
      if (index <= 1) {
        return "{id}";
      }
      return `{${array[index - 1]}Id}`;
    });
    return sanitizedParts.join("/");
    // eslint-disable-next-line no-catch-all/no-catch-all -- any failure must degrade to the raw url instead of breaking the caller's error path
  } catch (x) {
    captureException(x, {
      extra: {
        note: "Failed to sanitize URL",
        urlLength: url.length,
      },
    });

    return url;
  }
}
